import React, { forwardRef, useCallback } from 'react'
import { BasicSelectOption, Icon, InputRef, l, Select } from '@manychat/manyui'

import { useAssigneeOptions } from 'common/settings/components/BotAssigneePicker/useAssigneeOptions'
import { useFetchAssigneeData } from 'common/settings/components/BotAssigneePicker/useFetchAssigneeData'
import { SettingsTabType } from 'common/settings/constants/SettingsTabType'
import { typedRouting } from 'utils/router'

import { OnChangeTargetProps } from './interfaces'

interface BotAssigneePickerProps {
  value?: number | string | null
  disabled?: boolean
  invalid?: boolean
  onChange: (params: Record<'target', OnChangeTargetProps>) => void
  onFocus?: () => void
  onBlur?: () => void
  fetchData?: boolean
  id?: string
  errorText?: string
}

const withGroupsPlaceholder = l.makeTranslate('Choose a team member or a group')
const noUserFoundPlaceholder = l.makeTranslate('The user/group doesn`t exist')

export const BotAssigneePicker = forwardRef<InputRef, BotAssigneePickerProps>(
  ({ value, disabled, invalid, onFocus, onBlur, onChange, fetchData, id, errorText }, ref) => {
    const { isDataLoaded } = useFetchAssigneeData(fetchData)

    const { options, selectedOptionValue } = useAssigneeOptions(value)

    const handleChange = useCallback(
      (option: BasicSelectOption) => {
        const isGroup = option.type === 'item'

        const value = {
          target: {
            user_id: !isGroup ? Number(option.value) : null,
            group_id: isGroup ? Number(option.value) : null,
          },
        }
        onChange(value)
      },
      [onChange],
    )

    const handleRedirectSettings = () =>
      window.open(
        typedRouting.linkBuilder.settings({ hash: SettingsTabType.TEAM_MEMBERS }),
        '_blank',
      )

    const renderGoToTeamMembersButton = () => (
      <div
        className="row middle nowrap justify-center p-x p-y-xs b-t text-primary c-pointer"
        onMouseDown={(event) => event.preventDefault()}
        onClick={handleRedirectSettings}
        data-test-id="button-team-members"
      >
        <Icon.Plus className="m-r-xs" />
        <span>{l.translate('Team members')}</span>
      </div>
    )

    const hasValue = value !== null && value !== undefined

    const notFound = hasValue && selectedOptionValue === null

    const isInvalid = isDataLoaded && (invalid || notFound)
    const isDisabled = !isDataLoaded || disabled

    return (
      <div ref={ref}>
        <Select
          id={id}
          placeholder={l.getString(withGroupsPlaceholder)}
          ariaLabel={l.getString(withGroupsPlaceholder)}
          disabled={isDisabled}
          value={selectedOptionValue}
          invalid={isInvalid}
          options={options}
          errorText={errorText ?? l.getString(noUserFoundPlaceholder)}
          renderCustomLastComponent={renderGoToTeamMembersButton}
          onChange={handleChange}
          onFocus={onFocus}
          onBlur={onBlur}
          name="bot-assignee-picker"
        />
      </div>
    )
  },
)
